import { createSignal, For, Show, type JSX } from 'solid-js';
import { Slider } from '@kobalte/core/slider';
import './SliderRow.css';

export interface PresetDef {
  value: number;
  label: string;
  description?: string;
}

export interface SliderDef {
  id: string;
  badgeId?: string;
  label: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  value?: number;
  getDefaultValue?: () => number;
  formatBadge?: (v: number) => string;
  onChange?: (v: number) => void;
  onReset?: () => void;
  presets?: PresetDef[];
  presetsId?: string;
  alternateTicks?: boolean;
}

export function SliderRow(props: { def: SliderDef }): JSX.Element {
  const fmt = (): ((v: number) => string) => props.def.formatBadge ?? ((v: number): string => v.toFixed(1));
  const [localValue, setLocalValue] = createSignal(props.def.value ?? props.def.defaultValue);
  const value = (): number => props.def.value ?? localValue();
  const setValue = (v: number): void => { setLocalValue(v); };

  const ratio = (): number => {
    const range = props.def.max - props.def.min;
    return range > 0 ? (value() - props.def.min) / range : 0;
  };
  const pct = (): string => `${(ratio() * 100).toFixed(2)}%`;

  const handleChange = (vals: number[]): void => {
    const v = vals[0];
    setValue(v);
    props.def.onChange?.(v);
  };

  const applyPreset = (presetValue: number): void => {
    setValue(presetValue);
    props.def.onChange?.(presetValue);
  };

  const sortedPresets = (): PresetDef[] =>
    props.def.presets ? [...props.def.presets].sort((a, b) => a.value - b.value) : [];

  return (
    <div class="slider-row">
      <div class="slider-row-flex">
        <Slider
          value={[value()]}
          onChange={handleChange}
          minValue={props.def.min}
          maxValue={props.def.max}
          step={props.def.step}
          orientation="horizontal"
          class="slider-input-area"
          id={props.def.id}
        >
          <span class="slider-label-overlay">{props.def.label}</span>
          <input
            type="text"
            class="badge-input slider-badge-edit"
            id={props.def.badgeId}
            value={fmt()(value())}
            style={{ left: pct() }}
            onFocus={(e: FocusEvent): void => { (e.target as HTMLInputElement).select(); }}
            onChange={(e: Event): void => {
              const v = parseFloat((e.target as HTMLInputElement).value);
              if (Number.isFinite(v)) { setValue(Math.max(props.def.min, Math.min(props.def.max, v))); props.def.onChange?.(value()); }
            }}
          />
          <Slider.Track class="slider-track-el">
            <Slider.Fill class="slider-fill-el" />
            <Slider.Thumb class="slider-thumb-el">
              <Slider.Input />
            </Slider.Thumb>
          </Slider.Track>
          <Show when={props.def.presets}>
            <div class="slider-presets" id={props.def.presetsId}>
              <For each={sortedPresets()}>
                {(preset, i) => {
                  const pRatio = (): number => {
                    const range = props.def.max - props.def.min;
                    return range > 0 ? (preset.value - props.def.min) / range : 0;
                  };
                  const isActive = (): boolean => Math.abs(value() - preset.value) < 0.05;
                  const tickClass = (): string =>
                    props.def.alternateTicks && i() % 2 === 1 ? 'slider-tick slider-tick-staggered' : 'slider-tick slider-tick-long';
                  return (
                    <div
                      class="slider-preset-mark"
                      classList={{ active: isActive() }}
                      style={{ left: `${(pRatio() * 100).toFixed(2)}%` }}
                    >
                      <div class={tickClass()} />
                      <button
                        class="slider-preset-btn"
                        classList={{ active: isActive() }}
                        data-value={preset.value.toString()}
                        data-description={preset.description}
                        onClick={(): void => { applyPreset(preset.value); }}
                      >
                        {preset.label}
                      </button>
                    </div>
                  );
                }}
              </For>
            </div>
          </Show>
        </Slider>
      </div>
    </div>
  );
}
